import styled from 'styled-components'
import { ReactElement, useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import { useUserStore } from '../../../store/user/UserStore'
import { Spinner } from 'react-bootstrap'
import ShowNotification from '../../../ui-components/modals/ToastNotifcationNotiStack'
import { ToastNotificationVariant } from '../../../ui-components/util/Interfaces'
import UserInfoServiceOpProvider from '../../../services/UserInfoServiceOpProvider'
import UserInfoServiceProvider from '../../../services/UserInfoServiceProvider'
import { setUserSession, backupCurrentSession } from '../helper'
import { SalesPadOrg } from '../../../services/SalesPadServiceProvider'
import { defaultTheme } from '../../../theme'

interface Props {
  org: SalesPadOrg
}

const OrgBox = observer((props: Props): ReactElement => {
  const { org } = props
  const [isLoading, setIsLoading] = useState(false)
  const userStore = useUserStore()

  const [token, setToken] = useState<string | null>(null)

  const { refetch } = useQuery({
    queryKey: ['getCredentialsfromtoken'],
    queryFn: () => UserInfoServiceOpProvider.getCredentialsfromtoken(token),
    retry: 4,
    enabled: false,
  })

  useEffect(() => {
    if (!token) return
    refetch()
      .then((result) => {
        if (result.isSuccess && result.data) {
          setUserSession(result.data, 'true', 'true')
          window.location.reload()
        } else if (result.isError) {
          ShowNotification({
            message: 'contactUsFormErrorMessage',
            variant: ToastNotificationVariant.ERROR,
          })
        }
      })
      .finally(() => setIsLoading(false))
  }, [token, refetch])

  const handleClick = async (): Promise<void> => {
    try {
      setIsLoading(true)

      // Backup the current session before switching to a new organization
      backupCurrentSession()

      const orgId = org.childOrgId
      const key = await userStore.createSalesRepUserInfoCognito(orgId)
      const generatedToken = await UserInfoServiceProvider.generateV3Token(key)

      setToken(generatedToken)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
      setIsLoading(false)
      ShowNotification({
        message: 'contactUsFormErrorMessage',
        variant: ToastNotificationVariant.ERROR,
      })
    }
  }

  return (
    <Box onClick={handleClick} $isCSC={org.isCSC}>
      {isLoading ? <StyledSpinner /> : org.childOrgName}
    </Box>
  )
})

const Box = styled.div<{ $isCSC?: boolean }>`
  display: flex;
  padding: 20px;
  background-color: ${defaultTheme.colors.white};
  cursor: pointer;

  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  vertical-align: middle;

  box-shadow: ${({ $isCSC }) =>
    $isCSC
      ? `inset 0 0 0 4px ${defaultTheme.colors.blueLight}`
      : `inset 0 0 0 1px ${defaultTheme.colors.border}`};

  &:hover {
    box-shadow: ${({ $isCSC }) =>
      $isCSC
        ? `inset 0 0 0 4px ${defaultTheme.colors.blueLight}, inset 0 0 0 6px ${defaultTheme.colors.black}`
        : `inset 0 0 0 2px ${defaultTheme.colors.black}`};
  }
`

const StyledSpinner = styled(Spinner)`
  margin: auto;
`

export default OrgBox
